import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Paper, Typography, Switch, FormControlLabel, Grid, TextField,
  MenuItem, Divider, Button, CircularProgress, Card, CardContent, Chip, Alert, Fade
} from "@mui/material";
import {
  Diamond, AppRegistration, ShoppingBag, AccountBalanceWallet, Save, Settings, InfoOutlined
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchOfferConfigRequest, saveOfferConfigRequest } from "../../../redux/Offers/OfferSlice";
import {
  SchemeName, CalculationType, CustomerType, ExpireTimeUnit, SipTypes, OfferKeys, NumericFields
} from "../../../configs/constants";
import useOfferConfig from "../../../redux/Offers/useOfferConfig";
import { canShowPermission } from "../Permissions/permissionObject";
import { getAppConfig } from "../../../redux/Configuration/configurationSlice";

export default function DiamondPointsConfig() {
  const dispatch = useDispatch();
  let cal_type = CalculationType
  delete cal_type.Percentage;
  const enabledMetals = useSelector((state) => state.settingData?.data?.data?.metals || []);
  const { config: diamondConfig, loading } = useOfferConfig(OfferKeys.diamondpoints);
  const [offers, setOffers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [permissions, setPermissions] = useState({});

  const canCreate = canShowPermission(permissions, "diamondPoints", "create");
  const canEdit = canShowPermission(permissions, "diamondPoints", "edit");
  const canDelete = canShowPermission(permissions, "diamondPoints", "delete");

  // --- Logic Helpers ---
  const sipMetals = useMemo(() => [
    { metalName: SipTypes.MONEY, purity: null, _id: null },
    ...enabledMetals
  ], [enabledMetals]);

  useEffect(() => {
    dispatch(getAppConfig());
  }, [dispatch]);

  const initialOffer = useMemo(() => [
    {
      schemeName: SchemeName.DownloadApp,
      displayName: "App Installation Reward",
      note: "Points awarded once per user upon successful app registration.",
      enabled: false,
      config: { calculation_type: cal_type.Fixed, points: "", expireTime: "", expireTimeUnit: "" }
    },
    {
      schemeName: SchemeName.MetalPurchase,
      displayName: "Direct Purchase Points",
      note: "Points credited based on one-time metal buying transactions.",
      enabled: false,
      config: [] 
    },
    {
      schemeName: SchemeName.SipPurchase,
      displayName: "SIP Contribution Points",
      note: "Reward loyalty for consistent SIP installments (Metal & Money).",
      enabled: false,
      config: []
    }
  ], []);

  // --- Initialization & Hydration ---
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) setPermissions(JSON.parse(stored));
    dispatch(fetchOfferConfigRequest({ name: OfferKeys.diamondpoints }));
  }, [dispatch]);

  useEffect(() => {
    if (!enabledMetals.length) return;

    // This function ensures the UI renders all metals even if the DB config is empty
    const hydrateData = (savedData) => {
      return initialOffer.map(baseScheme => {
        const saved = savedData?.find(s => s.schemeName === baseScheme.schemeName);
        const scheme = saved ? { ...baseScheme, ...saved } : { ...baseScheme };

        // 1. Hydrate Metal Purchase (Dynamic Rows)
        if (scheme.schemeName === SchemeName.MetalPurchase) {
          const fullConfigs = [];
          [CustomerType.standard, CustomerType.vip].forEach(custType => {
            enabledMetals.forEach(metal => {
              const existing = Array.isArray(scheme.config) && scheme.config.find(
                c => c.custType === custType && c.metalName === metal.metalName && c.purity === metal.purity
              );
              fullConfigs.push(existing ? { ...existing, bullion: metal._id } : {
                custType, metalName: metal.metalName, purity: metal.purity, bullion: metal._id,
                initialTransactions: "", initialTransactionPoints: "", afterInitalTransactionsPoints: "",
                calculation_type: cal_type.Fixed, expireTime: "", expireTimeUnit: "", enabled: false
              });
            });
          });
          scheme.config = fullConfigs;
        }

        // 2. Hydrate SIP Purchase (Dynamic Rows)
        if (scheme.schemeName === SchemeName.SipPurchase) {
          const fullConfigs = [];
          [CustomerType.standard, CustomerType.vip].forEach(custType => {
            sipMetals.forEach(metal => {
              const existing = Array.isArray(scheme.config) && scheme.config.find(
                c => c.custType === custType && c.metalName === metal.metalName && c.purity === metal.purity
              );
              fullConfigs.push(existing ? { ...existing, bullion: metal._id } : {
                custType, metalName: metal.metalName, purity: metal.purity, bullion: metal._id,
                startPoints: "", installmentPoints: "", endPoints: "",
                calculation_type: cal_type.Fixed, expireTime: "", expireTimeUnit: "", enabled: false
              });
            });
          });
          scheme.config = fullConfigs;
        }
        return scheme;
      });
    };

    setOffers(hydrateData(diamondConfig));
  }, [diamondConfig, enabledMetals, initialOffer, sipMetals]);

  // --- Handlers ---
  const handleToggle = (idx) => (e) => {
    setOffers(prevOffers => {
      const updated = [...prevOffers];
      updated[idx] = { ...updated[idx], enabled: e.target.checked };
      return updated;
    });
  };

  const handleConfigToggle = (sIdx, cIdx, checked) => {
    setOffers(prevOffers => {
      const updated = [...prevOffers];
      const scheme = { ...updated[sIdx] };
      const config = [...scheme.config];
      config[cIdx] = { ...config[cIdx], enabled: checked };
      scheme.config = config;
      updated[sIdx] = scheme;
      return updated;
    });
  };

  const handleConfigChange = (sIdx, cIdx, key, val) => {
    setOffers(prev => {
      const newOffers = [...prev];
      const scheme = { ...newOffers[sIdx] };
      if (cIdx === null) {
        scheme.config = { ...scheme.config, [key]: val };
      } else {
        const newConfig = [...scheme.config];
        newConfig[cIdx] = { ...newConfig[cIdx], [key]: val };
        scheme.config = newConfig;
      }
      newOffers[sIdx] = scheme;
      return newOffers;
    });

    if (errors[`${sIdx}-${cIdx}-${key}`] || errors[`${sIdx}-${key}`]) {
      setErrors(prev => {
        const { [`${sIdx}-${cIdx}-${key}`]: _, [`${sIdx}-${key}`]: __, ...rest } = prev;
        return rest;
      });
    }
  };

  const validate = () => {
    let newErrors = {};
    let isValid = true;

    offers.forEach((scheme, sIdx) => {
      if (!scheme.enabled) return;
      if (!Array.isArray(scheme.config)) {
        Object.entries(scheme.config).forEach(([key, val]) => {
          if (!val && val !== 0) {
            newErrors[`${sIdx}-${key}`] = "Required"; isValid = false;
          }
        });
      } else {
        scheme.config.forEach((conf, cIdx) => {
          if (!conf.enabled) return;
          Object.entries(conf).forEach(([key, val]) => {
            if (["enabled", "purity", "bullion", "custType", "metalName"].includes(key)) return;
            if (!val && val !== 0) {
              newErrors[`${sIdx}-${cIdx}-${key}`] = "Required"; isValid = false;
            }
            if (conf.calculation_type === cal_type.Percentage) {
              if (NumericFields.includes(key) && Number(val) > 100) {
                newErrors[`${sIdx}-${cIdx}-${key}`] = "Cannot exceed 100%"; isValid = false;
              }
            }
          });
        });
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const onSave = () => {
    if (!validate()) return;
    setSaving(true);
    dispatch(saveOfferConfigRequest({
      name: OfferKeys.diamondpoints,
      value: offers,
      callback: (res) => {
        setSaving(false);
        if (res?.status) setErrors({});
      }
    }));
  };

  if (loading) return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="60vh">
      <CircularProgress size={60} thickness={4} sx={{ color: '#752ADA' }} />
      <Typography mt={2} fontWeight={600} color="text.secondary">Syncing Diamond Configuration</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1300, margin: "auto" }}>
      <Box display="flex" alignItems="center" mb={5}>
        <Diamond sx={{ color: "#00d4ff", fontSize: 50, mr: 2, filter: "drop-shadow(0px 4px 10px rgba(0,212,255,0.4))" }} />
        <Box>
          <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: -1 }}>DIAMOND POINT CONFIGURATION</Typography>
          <Typography color="text.secondary" variant="subtitle1">Configure point generation rules across the ecosystem</Typography>
        </Box>
      </Box>

      {offers.map((scheme, sIdx) => (
        <Card key={scheme.schemeName} sx={{ 
          mb: 4, borderRadius: 4, transition: '0.3s',
          borderLeft: scheme.enabled ? '10px solid #752ADA' : '10px solid #e0e0e0',
          boxShadow: scheme.enabled ? 6 : 1,
          '&:hover': { boxShadow: 10 }
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
              <Box>
                <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                  {scheme.schemeName === SchemeName.DownloadApp ? <AppRegistration color="primary" /> : 
                   scheme.schemeName === SchemeName.MetalPurchase ? <ShoppingBag color="primary" /> : <AccountBalanceWallet color="primary" />}
                  <Typography variant="h5" fontWeight={800}>{scheme.displayName}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">{scheme.note}</Typography>
              </Box>
              <Box textAlign="right">
                <Chip label={scheme.enabled ? "LIVE" : "PAUSED"} color={scheme.enabled ? "secondary" : "default"} sx={{ fontWeight: 900, mr: 2 }} />
                <Switch checked={scheme.enabled} onChange={handleToggle(sIdx)} color="secondary" disabled={!canEdit} />
              </Box>
            </Box>

            {scheme.enabled && (
              <Fade in={scheme.enabled}>
                <Box>
                  <Divider sx={{ my: 3 }} />
                  {!Array.isArray(scheme.config) ? (
                    <Grid container spacing={3}>
                      {Object.keys(scheme.config).map(key => (
                        <Grid item xs={12} md={3} key={key}>
                          <TextField
                            fullWidth select={key.includes('Unit') || key.includes('type')}
                            label={key.replace(/([A-Z])/g, ' $1').toUpperCase()}
                            value={scheme.config[key]}
                            error={!!errors[`${sIdx}-${key}`]}
                            helperText={errors[`${sIdx}-${key}`]}
                            onChange={(e) => handleConfigChange(sIdx, null, key, e.target.value)}
                            disabled={!canEdit}
                          >
                            {key === "calculation_type" ? <MenuItem value={cal_type.Fixed}>Fixed</MenuItem> :
                             Object.values(ExpireTimeUnit).map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                          </TextField>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Grid container spacing={3}>
                      {scheme.config.map((conf, cIdx) => (
                        <Grid item xs={12} key={cIdx}>
                          <Paper variant="outlined" sx={{ 
                            p: 3, borderRadius: 3, 
                            bgcolor: conf.enabled ? (conf.custType === CustomerType.vip ? '#fffdf0' : '#f0f7ff') : '#fafafa',
                            border: conf.enabled ? `1.5px solid ${conf.custType === CustomerType.vip ? '#ffd700' : '#1976d2'}` : '1px solid #e0e0e0'
                          }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip 
                                  label={conf.custType.toUpperCase()} 
                                  size="small" 
                                  sx={{ bgcolor: conf.custType === CustomerType.vip ? '#ffd700' : '#1976d2', color: '#fff', fontWeight: 900 }} 
                                />
                                <Typography fontWeight={700}>
                                  {conf.metalName} {conf.purity ? `(${conf.purity})` : ''}
                                </Typography>
                              </Box>
                              <FormControlLabel
                                control={<Switch 
                                    size="small" 
                                    checked={conf.enabled} 
                                    onChange={(e) => handleConfigToggle(sIdx, cIdx, e.target.checked)} 
                                    sx={{ ml: 2 }}
                                    disabled={!canEdit}
                                />}
                                label="Enable"
                              />
                            </Box>

                            {conf.enabled && (
                              <Grid container spacing={2}>
                                {Object.keys(conf).map(key => {
                                  if (["enabled", "custType", "metalName", "purity", "bullion"].includes(key)) return null;
                                  const isSelect = key.includes('Unit') || key === "calculation_type";
                                  return (
                                    <Grid item xs={12} md={4} lg={3} key={key}>
                                      <TextField
                                        fullWidth size="small" select={isSelect} 
                                        label={key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                        value={conf[key]}
                                        error={!!errors[`${sIdx}-${cIdx}-${key}`]}
                                        helperText={errors[`${sIdx}-${cIdx}-${key}`]}
                                        type={NumericFields.includes(key) && !isSelect ? "number" : "text"}
                                        onChange={(e) => handleConfigChange(sIdx, cIdx, key, e.target.value)}
                                        disabled={!canEdit}
                                      >
                                        {isSelect && (key === "calculation_type" 
                                          ? Object.values(cal_type).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)
                                          : Object.values(ExpireTimeUnit).map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)
                                        )}
                                      </TextField>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Fade>
            )}
          </CardContent>
        </Card>
      ))}

      <Box sx={{ height: 100 }} />
      <Paper sx={{ 
        position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, 
        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3,
        backdropFilter: 'blur(10px)', bgcolor: 'rgba(255,255,255,0.8)', borderTop: '1px solid #ddd', zIndex: 1000
      }}>
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" variant="filled" icon={<InfoOutlined />} sx={{ borderRadius: 2 }}>
            Missing required fields in enabled rules
          </Alert>
        )}
        {(canCreate || canEdit) && (
          <Button
            variant="contained" size="large" startIcon={<Save />}
            onClick={onSave} disabled={saving}
            sx={{ 
              bgcolor: "#752ADA", px: 10, borderRadius: 5, fontWeight: 800,
              boxShadow: '0 8px 20px rgba(117, 42, 218, 0.3)',
              '&:hover': { bgcolor: '#5e1fb3' }
            }}
          >
            {saving ? "Saving..." : "Set Configuration"}
          </Button>
        )}
      </Paper>
    </Box>
  );
}